import { useMemo } from "react";
import { DOSSIER_TAB_PERMISSION_KEYS } from "../../config/applicationHubPermissions";
import { CONSOLE_PERMISSION_MODULES } from "../../config/consolePermissionModules";

function moduleAllKeys(m: (typeof CONSOLE_PERMISSION_MODULES)[number]): string[] {
  const keys = [
    ...(m.prefixGranularKeys ?? []),
    ...(m.exclusiveFullKey ? [m.exclusiveFullKey] : []),
    ...(m.postExclusiveGranularKeys ?? []),
    ...m.granularKeys,
  ];
  return keys;
}

const MODULE_COVERED_KEYS = new Set(CONSOLE_PERMISSION_MODULES.flatMap(moduleAllKeys));

type Props = {
  keys: string[];
  labelsById: Map<string, string>;
  value: Record<string, boolean>;
  onChange: (next: Record<string, boolean>) => void;
};

export function PermissionModulePicker({ keys, labelsById, value, onChange }: Props) {
  const baseline = useMemo(
    () => Object.fromEntries(keys.map((k) => [k, Boolean(value[k])])) as Record<string, boolean>,
    [keys, value],
  );

  const merge = (patch: Record<string, boolean>) => {
    const next = { ...baseline, ...patch };
    onChange(next);
  };

  const unknownKeys = useMemo(() => keys.filter((k) => !MODULE_COVERED_KEYS.has(k)), [keys]);

  return (
    <div className="permission-module-picker">
      {CONSOLE_PERMISSION_MODULES.map((module) => {
        if (module.infoOnly) {
          return (
            <fieldset key={module.id} className="permission-module-picker__module permission-module-picker__module--info">
              <legend className="permission-module-picker__legend">{module.title}</legend>
              {module.description ? (
                <p className="permission-module-picker__desc permission-module-picker__desc--info">{module.description}</p>
              ) : null}
            </fieldset>
          );
        }

        const exclusiveFk = module.exclusiveFullKey;
        const fullLocked = Boolean(exclusiveFk && baseline[exclusiveFk]);
        const prefixKeys = module.prefixGranularKeys ?? [];
        const postExclusiveKeys = module.postExclusiveGranularKeys ?? [];
        const granularKeys = module.granularKeys;
        const allTogglable = [...prefixKeys, ...granularKeys];
        const hasToolbar = !exclusiveFk && allTogglable.length > 0;

        return (
          <fieldset key={module.id} className="permission-module-picker__module">
            <legend className="permission-module-picker__legend">{module.title}</legend>
            {module.description ? <p className="permission-module-picker__desc">{module.description}</p> : null}

            {prefixKeys.length > 0 ? (
              <div className="permission-module-picker__subsection">
                {prefixKeys.map((k) => (
                  <label key={k} className="console-users-page__perm-row permission-module-picker__row">
                    <input
                      type="checkbox"
                      className="console-users-page__perm-check"
                      checked={Boolean(baseline[k])}
                      onChange={() => merge({ [k]: !baseline[k] })}
                    />
                    <span className="console-users-page__perm-label">{labelsById.get(k) ?? k}</span>
                  </label>
                ))}
              </div>
            ) : null}

            {exclusiveFk ? (
              <label className="permission-module-picker__full-row">
                <input
                  type="checkbox"
                  className="console-users-page__perm-check"
                  checked={Boolean(baseline[exclusiveFk])}
                  onChange={() => {
                    const on = !baseline[exclusiveFk];
                    const patch: Record<string, boolean> = { [exclusiveFk]: on };
                    if (on && exclusiveFk === "applications") {
                      DOSSIER_TAB_PERMISSION_KEYS.forEach((k) => {
                        patch[k] = false;
                      });
                    }
                    merge(patch);
                  }}
                />
                <span className="console-users-page__perm-label">{labelsById.get(exclusiveFk) ?? exclusiveFk}</span>
              </label>
            ) : hasToolbar ? (
              <div className="permission-module-picker__toolbar">
                <button
                  type="button"
                  className="btn btn--tg-secondary btn--sm"
                  onClick={() => {
                    const allOn = allTogglable.every((k) => baseline[k]);
                    const patch: Record<string, boolean> = {};
                    allTogglable.forEach((k) => {
                      patch[k] = !allOn;
                    });
                    merge(patch);
                  }}
                >
                  {allTogglable.every((k) => baseline[k]) ? "Clear module" : "Select entire module"}
                </button>
              </div>
            ) : null}

            {postExclusiveKeys.length > 0 ? (
              <div className="permission-module-picker__subsection">
                {postExclusiveKeys.map((k) => (
                  <label key={k} className="console-users-page__perm-row permission-module-picker__row">
                    <input
                      type="checkbox"
                      className="console-users-page__perm-check"
                      checked={Boolean(baseline[k])}
                      onChange={() => merge({ [k]: !baseline[k] })}
                    />
                    <span className="console-users-page__perm-label">{labelsById.get(k) ?? k}</span>
                  </label>
                ))}
              </div>
            ) : null}

            {granularKeys.length > 0 ? (
              <div
                className={
                  "permission-module-picker__granular" +
                  (fullLocked ? " permission-module-picker__granular--disabled" : "")
                }
                aria-disabled={fullLocked || undefined}
              >
                {granularKeys.map((k) => (
                  <label key={k} className="console-users-page__perm-row permission-module-picker__row">
                    <input
                      type="checkbox"
                      className="console-users-page__perm-check"
                      disabled={fullLocked}
                      checked={Boolean(baseline[k])}
                      onChange={() => {
                        if (fullLocked) return;
                        const patch: Record<string, boolean> = { [k]: !baseline[k] };
                        if (exclusiveFk) {
                          patch[exclusiveFk] = false;
                        }
                        merge(patch);
                      }}
                    />
                    <span className="console-users-page__perm-label">{labelsById.get(k) ?? k}</span>
                  </label>
                ))}
              </div>
            ) : null}
          </fieldset>
        );
      })}

      {unknownKeys.length > 0 ? (
        <fieldset className="permission-module-picker__module">
          <legend className="permission-module-picker__legend">Other permissions</legend>
          <p className="permission-module-picker__desc">Keys from the server not listed in the modules above.</p>
          <div className="permission-module-picker__granular">
            {unknownKeys.map((k) => (
              <label key={k} className="console-users-page__perm-row permission-module-picker__row">
                <input
                  type="checkbox"
                  className="console-users-page__perm-check"
                  checked={Boolean(baseline[k])}
                  onChange={() => merge({ [k]: !baseline[k] })}
                />
                <span className="console-users-page__perm-label">{labelsById.get(k) ?? k}</span>
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}
    </div>
  );
}
